// MST
import { types, flow, cast } from 'mobx-state-tree'

// Interfaces
import { IMyDocumentDetail, IMyDocumentValidation } from 'interfaces/back-office/personal-data/my-document'
import {
  IMyDocumentApiPaginatedResponse,
  IMyDocumentApiGetAllResponse,
  IMyDocumentApiGetResponse,
  IMyDocumentApiDownloadDocumentResponse,
  IMyDocumentApiPreviewDocumentResponse,
  IMyDocumentApiValidateDocumentDetailResponse,
} from 'interfaces/back-office/personal-data/my-document/my-document-api.interface'
import {
  IMyDocumentGetAllAttrs,
  IMyDocumentSaveAttrs,
  IMyDocumentGetAttrs,
  IMyDocumentDownloadDocumentAttrs,
  IMyDocumentPreviewDocumentAttrs,
  IMyDocumentValidateAttrs,
  IMyDocumentDeleteAttrs
} from 'interfaces/back-office/personal-data/my-document/my-document-view.interface'

// Extensions
import { withHelper_baseResponsePaginationTypes, withHelper_fileDownload } from 'models/extensions/with-helper'

// Utils
import { withEnvironment } from 'models/extensions/with-environment'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'
import { dateUtils_formatDate } from 'utils/common/date.utils'

// Lodash
import pick from 'lodash/pick'

// Types
const { model, maybeNull, optional, string, number, boolean } = types

// My Document Props
const myDocumentProps = {
  number: maybeNull(number),
  id: maybeNull(number),
  jenisPrasyaratName: maybeNull(string),
  jenisPrasyaratId: maybeNull(number),
  documentProofId: maybeNull(number),
  documentProofName: maybeNull(string),
  uploadDate: maybeNull(string),
  userFileBlobId: maybeNull(string),
  userOriginalFileName: maybeNull(string),
  description: maybeNull(string),
  poinKum: maybeNull(string)
}

// My Document Detail
const MyDocumentDetailModel = model('MyDocumentDetail').props(myDocumentProps)

// My Document
const MyDocumentsModel = model('MyDocuments').props(
  pick(myDocumentProps, [
    'id',
    'number',
    'jenisPrasyaratName',
    'documentProofName',
    'uploadDate',
    'userFileBlobId',
    'userOriginalFileName',
    'description',
    'poinKum'
  ]),
)

// My Document Paginated
const MyDocumentModelPaginated = model('MyDocumentPaginated').props({
  ...withHelper_baseResponsePaginationTypes(MyDocumentsModel), 
  totalPoinKum: maybeNull(number)
})

// My Document Validation
const MyDocumentValidationModel = model('MyDocumentValidation').props({
  isRegistered: optional(boolean, false),
})

// Base Model
const MyDocumentStoreModel = types
  .model('MyDocument')
  .props({
    myDocument_isLoading: optional(boolean, false),
    myDocument_isSaveLoading: optional(boolean, false),
    myDocument_isValidationLoading: optional(boolean, false),
    myDocument_isDeleteLoading: optional(boolean, false),
    myDocument_myDocuments: optional(MyDocumentModelPaginated, {}),
    myDocument_detail: optional(MyDocumentDetailModel, {}),
    myDocument_validation: optional(MyDocumentValidationModel, {}),
  })
  .extend(withEnvironment)
  .actions(self => ({
    myDocument_SET_LOADING: (payload: boolean): void => {
      self.myDocument_isLoading = payload
    },
    myDocument_SET_SAVE_LOADING: (payload: boolean): void => {
      self.myDocument_isSaveLoading = payload
    },
    myDocument_SET_VALIDATE_LOADING: (payload: boolean) => {
      self.myDocument_isValidationLoading = payload
    },
    myDocument_SET_DELETE_LOADING: (payload: boolean): void => {
      self.myDocument_isDeleteLoading = payload
    },
    myDocument_SET_MY_DOCUMENTS: (payload: IMyDocumentApiPaginatedResponse): void => {
      self.myDocument_myDocuments = cast({
        ...payload,
        list: payload.list.map((list, index) => ({
          ...list,
          number: commonUtils_renderTableNumber(index, payload),
          uploadDate: dateUtils_formatDate(list.uploadDate!, { tableFormat: true }),
        })),
      })
    },
    myDocument_SET_MY_DOCUMENT: (payload: IMyDocumentDetail): void => {
      self.myDocument_detail = cast({ ...payload })
    },
    myDocument_SET_VALIDATION: (payload: IMyDocumentValidation): void => {
      self.myDocument_validation = cast(payload)
    },
    myDocument_CLEAR_MY_DOCUMENTS: (): void => {
      self.myDocument_myDocuments = cast({})
    },
    myDocument_CLEAR_MY_DOCUMENT: (): void => {
      self.myDocument_detail = cast({})
    },
    myDocument_CLEAR_VALIDATION: (): void => {
      self.myDocument_validation = cast({})
    },
  }))
  .actions(self => ({
    /**
     * @description Fetch my documents
     *
     * @param {IMyDocumentGetAllAttrs} payload
     *
     * @return {any} any
     */
    myDocument_fetchMyDocuments: flow(function* (payload: IMyDocumentGetAllAttrs) {
      self.myDocument_SET_LOADING(true)

      const response: IMyDocumentApiGetAllResponse = yield self.environment.myDocumentApi.myDocument_getMyDocuments({
        body: payload.body,
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.myDocument_SET_MY_DOCUMENTS(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.myDocument_SET_LOADING(false)
    }),

    /**
     * @description Fetch my documents for a spesific user (used in user detail)
     *
     * @param {IMyDocumentGetAllAttrs} payload
     *
     * @return {any} any
     */
    myDocument_fetchMyDocumentsByUserId: flow(function* (payload: IMyDocumentGetAllAttrs) {
      self.myDocument_SET_LOADING(true)

      const response: IMyDocumentApiGetAllResponse = yield self.environment.myDocumentApi.myDocument_getMyDocumentsByUserId({
        body: payload.body,
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.myDocument_SET_MY_DOCUMENTS(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.myDocument_SET_LOADING(false)
    }),

    /**
     * @description Fetch my document
     *
     * @param {IMyDocumentGetAttrs} payload
     *
     * @return {any} any
     */
    myDocument_fetchMyDocument: flow(function* (payload: IMyDocumentGetAttrs) {
      self.myDocument_SET_LOADING(true)

      const response: IMyDocumentApiGetResponse = yield self.environment.myDocumentApi.myDocument_getMyDocument({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.myDocument_SET_MY_DOCUMENT(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.myDocument_SET_LOADING(false)
    }),

    /**
     * @description Save my document
     *
     * @param {IMyDocumentSaveAttrs} payload
     *
     * @return {any} any
     */
    myDocument_saveMyDocument: flow(function* (payload: IMyDocumentSaveAttrs) {
      self.myDocument_SET_SAVE_LOADING(true)

      const response = yield self.environment.myDocumentApi.myDocument_saveMyDocument({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        // Do when response is success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.myDocument_SET_SAVE_LOADING(false)
    }),

    /**
     * @description Download Document
     *
     * @param {IMyDocumentDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    myDocument_downloadDocument: flow(function* (payload: IMyDocumentDownloadDocumentAttrs) {
      const response: IMyDocumentApiDownloadDocumentResponse = yield self.environment.myDocumentApi.myDocument_downloadDocument({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        withHelper_fileDownload(response.res, response.headers!)

        // Do when response is success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response is failed
        if (payload?.onError) payload.onError()
      }
    }),

    /**
     * @description Preview or Download document given User ID and Blob ID
     *
     * @param {IMyDocumentPreviewDocumentAttrs} payload
     *
     * @return {any} any
     */
    myDocument_previewDocumentByUserId: flow(function* (payload: IMyDocumentPreviewDocumentAttrs) {
      const response: IMyDocumentApiPreviewDocumentResponse = yield self.environment.myDocumentApi.myDocument_previewDocumentByUserId({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        let type = ''
        if (payload.params.originalFileName?.includes('.pdf')) {
          type = 'application/pdf'
        } else if (payload.params.originalFileName?.toLocaleLowerCase().includes('.jpg') || payload.params.originalFileName?.toLocaleLowerCase().includes('.jpeg') || payload.params.originalFileName?.toLocaleLowerCase().includes('.png')) {
          const fileExtension = payload.params.originalFileName?.toLocaleLowerCase().includes('.jpg') ? 'jpg' 
                                : payload.params.originalFileName?.toLocaleLowerCase().includes('.jpeg') ? 'jpeg' 
                                : payload.params.originalFileName?.toLocaleLowerCase().includes('.png') ? 'png'
                                : ''
          type = `image/${fileExtension}`
        } else {
          withHelper_fileDownload(response.res, response.headers!)
          // Do when response is success
          if (payload?.onSuccess) payload.onSuccess() 
          return 
        }
        const file = new File([response.res], payload.params.originalFileName, {
          type: type
        });
        const url = URL.createObjectURL(file);
        window.open(url)

        // Do when response is success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response is failed
        if (payload?.onError) payload.onError()
      }
    }),

    /**
     * @description Validate Form
     *
     * @param {IMyDocumentValidateAttrs} payload
     *
     * @return {any} any
     */
    myDocument_validate: flow(function* (payload: IMyDocumentValidateAttrs) {
      self.myDocument_SET_VALIDATE_LOADING(true)

      const response: IMyDocumentApiValidateDocumentDetailResponse = yield self.environment.myDocumentApi.myDocument_validate({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.myDocument_SET_VALIDATION(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response is failed
        if (payload?.onError) payload.onError()
      }

      self.myDocument_SET_VALIDATE_LOADING(false)
    }),

    /**
     * @description Delete my document
     *
     * @param {IMyDocumentDeleteAttrs} payload
     *
     * @return {any} any
     */
    myDocument_deleteMyDocument: flow(function* (payload: IMyDocumentDeleteAttrs) {
      self.myDocument_SET_DELETE_LOADING(true)

      const response = yield self.environment.myDocumentApi.myDocument_deleteMyDocument({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        // Do when response is success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.myDocument_SET_DELETE_LOADING(false)
    }),
  }))

export const createMyDocumentStoreDefaultModel = () => optional(MyDocumentStoreModel, {})
